import { generatePalette } from "../theme/themeUtils";
import { DEFAULT_PRIMARY } from "../theme/palettes";

const defaultPalette = generatePalette(DEFAULT_PRIMARY);

/**
 * Tailwind slate renk sabitleri — hardcoded hex yerine bunları kullanın.
 * Bileşenlerde: import { COLORS } from "styles/uiConstants";
 */
export const COLORS = {
  white: "#ffffff",
  black: "#0f172a",

  // Slate grays (borders, backgrounds, text)
  slate50: "#f8fafc",
  slate100: "#f1f5f9",
  slate200: "#e2e8f0",
  slate300: "#cbd5e1",
  slate400: "#94a3b8",
  slate500: "#64748b",
  slate600: "#475569",
  slate700: "#334155",
  slate800: "#1e293b",

  // Semantic status
  successLight: "#dcfce7",
  success: "#22c55e",
  successDark: "#16a34a",
  successDarker: "#166534",

  errorLightest: "#fef2f2",
  errorLight: "#fee2e2",
  error: "#ef4444",
  errorDark: "#dc2626",

  warningLightest: "#fffbeb",
  warningLight: "#fef3c7",
  warning: "#f59e0b",
  warningDark: "#d97706",
  warningDarker: "#92400e",

  // Kategorik chip tonları — rol/durum gibi ayrık kategorileri birbirinden
  // ayırmak için. Tema rengiyle İLGİSİ YOKTUR: klinik teması değişse de sabit
  // kalırlar, çünkü işlevleri vurgulamak değil kategori ayırt ettirmek.
  catBlueLight: "#e3f2fd",
  catBlueDark: "#1565c0",
  catPurpleLight: "#f3e5f5",
  catPurpleDark: "#6a1b9a",
  catPinkLight: "#fce4ec",
  catPinkDark: "#ad1457",
};

/**
 * Dashboard bölüm/stat accent renkleri. Kategorik çipler (cat*) gibi tema
 * renginden BAĞIMSIZDIR: klinik teması değişse de sabit kalırlar, çünkü
 * işlevleri vurgulamak değil bölümleri birbirinden ayırt ettirmektir.
 * Dashboard sayfalarındaki hardcoded hex'ler burada toplanır.
 */
export const ACCENTS = {
  blue: "#3b82f6",
  violet: "#8b5cf6",
  cyan: "#06b6d4",
  indigo: "#6366f1",
  amber: COLORS.warning,
  emerald: COLORS.success,
  slate: COLORS.slate500,
};

export const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: COLORS.white,
    boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
    "& fieldset": { borderColor: COLORS.slate200 },
    "&:hover fieldset": { borderColor: "#cbd5f3" },
    "&.Mui-focused fieldset": { borderColor: defaultPalette.primary },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 700,
    color: COLORS.slate600,
  },
};

/**
 * Kart aralık standardı (MUI spacing birimi, 1 = 8px) — tüm kartlı sayfalar.
 * CARD_PAD: kart başlığı, InfoRow satırları ve kart gövdesinin iç boşluğu (16px).
 * CARD_GAP: yan yana / alt alta kartlar arası boşluk (16px).
 * Değiştirmek gerekirse yalnız burası değişir.
 */
export const CARD_PAD = 2;
export const CARD_GAP = 2;
/** Kartlı sekme/sayfa içerik kabının kenar boşluğu — yatayda başlık şeridiyle aynı hiza. */
export const CARD_PAGE_PAD = { px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } };

/**
 * Dashboard kart dili — beyaz gövde, ince slate border, düşük gölge.
 * Hover-lift YOKTUR: kartlar sabit durur, hareket sadece veri değişiminde olur.
 * `dashboardCardSx` kendi padding'ini taşıyan kartlar (stat kutuları, header),
 * `dashboardSectionSx` ise SectionCard'a geçirilen bölüm kartları içindir.
 */
export const dashboardCardSx = {
  borderRadius: 3,
  p: 2.5,
  bgcolor: COLORS.white,
  border: `1px solid ${COLORS.slate200}`,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

export const dashboardSectionSx = {
  borderRadius: 3,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

export const drawerSectionSx = {
  p: 2.5,
  borderRadius: 3,
  border: `1px solid ${COLORS.slate200}`,
  background: `linear-gradient(180deg, ${COLORS.white} 0%, ${COLORS.slate50} 100%)`,
  boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
};

export const drawerHeaderSx = {
  px: 3,
  py: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: `linear-gradient(135deg, #eff6ff 0%, ${COLORS.white} 60%)`,
  borderBottom: `1px solid ${COLORS.slate200}`,
};

export const tableHeadCellSx = {
  fontWeight: 900,
  background: defaultPalette.primary,
  color: COLORS.white,
  borderBottom: "none",
};

export const emptyStateCardSx = {
  background: "rgba(255,255,255,0.96)",
  border: `1px solid ${COLORS.slate200}`,
  borderRadius: 4,
  px: 4,
  py: 3.5,
  textAlign: "center",
  boxShadow: "0 16px 40px rgba(15,23,42,0.12)",
  minWidth: 260,
};

export const emptyStateIconSx = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: `linear-gradient(135deg, ${defaultPalette.primaryLighter} 0%, ${defaultPalette.chipPatientBg} 100%)`,
  color: defaultPalette.primary,
};

/**
 * Palette-aware sx objeleri üreten fonksiyonlar.
 * Bileşenler: const { palette } = useClinicTheme(); ile alıp bu fn'lere geçer.
 */
export function themedInputSx(palette) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: COLORS.white,
      boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
      "& fieldset": { borderColor: COLORS.slate200 },
      "&:hover fieldset": { borderColor: palette.inputBorder },
      "&.Mui-focused fieldset": { borderColor: palette.primary },
    },
    "& .MuiInputLabel-root": {
      fontWeight: 700,
      color: COLORS.slate600,
    },
  };
}

export function themedTableHeadCellSx(palette) {
  return {
    fontWeight: 700,
    fontSize: "0.8rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    background: COLORS.slate50,
    color: COLORS.slate600,
    borderBottom: `2px solid ${COLORS.slate200}`,
    py: 1.5,
    whiteSpace: "nowrap",
  };
}

export function themedEmptyStateIconSx(palette) {
  return {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${palette.primaryLighter} 0%, ${palette.chipPatientBg} 100%)`,
    color: palette.primary,
  };
}

export function themedDrawerHeaderSx(palette) {
  return {
    px: 3,
    py: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: `linear-gradient(135deg, ${palette.chipPatientBg} 0%, ${COLORS.white} 60%)`,
    borderBottom: `1px solid ${COLORS.slate200}`,
  };
}

/**
 * Ortak tipografi ölçeği. Liste sayfaları, drawer'lar ve dialog'lar aynı
 * ölçeği kullanır — MUI'nin varsayılan h6/body1 ölçeği bu panelde fazla iri
 * kalıyordu (drawer bölüm başlığı sayfa başlığından büyüktü).
 *
 * Değerler liste desenindeki fiili kullanımdan türetildi:
 *   tablo başlığı 0.75rem · hücre 0.8125rem · ikincil 0.6875rem
 *   toolbar başlığı 1.0625rem · toolbar alt başlığı 0.75rem
 */
export const TYPO = {
  title: "1.0625rem",      // drawer/sayfa üst başlığı
  subtitle: "0.75rem",     // üst başlığın altındaki açıklama
  sectionTitle: "0.8125rem", // bölüm başlığı (eski variant="h6"), fontWeight 700 ile
  body: "0.8125rem",       // asıl içerik / alan değeri
  label: "0.75rem",        // alan etiketi
  caption: "0.6875rem",    // ikincil, küçük metin
};
